import { Fragment, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { authApi } from '../api/auth.js';
import { tokens } from '../api/cliente.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Icone } from '../ui/Icone.js';
import { Avatar, Botao, BotaoDeIcone } from '../ui/primitivos.jsx';
import { Modal, CorpoDeModal } from '../ui/Modal.jsx';
import { TemaToggle } from '../ui/TemaToggle.jsx';
import { siglaDe } from '../ui/sigla.js';
import { useToast } from '../ui/Toast.jsx';
import { useBuscaGlobal, type ResultadoDeBusca } from './buscaGlobal.js';
import { MENU, menuVisivel } from './navegacao.js';

/**
 * O shell: sidebar, topbar e a área de conteúdo.
 *
 * A marca no topo do menu é a do PRODUTO — Sand Sports. A arena é um
 * espaço dentro dele, e aparece no seletor logo abaixo e na trilha da
 * barra de cima, sempre como dado (o nome vem da API), nunca como
 * identidade visual. É isso que faz o sistema servir a várias arenas.
 *
 * A sidebar é montada a partir da matriz da sessão. Entrar como
 * Professor faz os grupos Dinheiro, Inteligência e Configuração
 * DESAPARECEREM — não ficarem cinzas —, e o botão "Nova reserva" da
 * topbar sumir. Esconder em vez de desabilitar é a leitura certa: um
 * menu cheio de itens bloqueados ensina a pessoa a ignorar o menu.
 *
 * Ver: Arena_backend/Docs/plano/05-front-fundacao.md
 */
export function Shell() {
  const { eu, pode, sair } = useSessao();
  const navegar = useNavigate();
  const local = useLocation();
  const avisar = useToast();
  const [trocandoArena, setTrocandoArena] = useState(false);
  // As notas explicativas do protótipo ("Módulo 8 — Dashboard executivo")
  // eram para a apresentação ao dono. No produto elas começam desligadas;
  // o interruptor no rodapé do menu as traz de volta.
  const [mostrarNotas, setMostrarNotas] = useState(false);

  // A mesma consulta do modal de troca de arena: o React Query a
  // compartilha, então abrir o modal não faz uma segunda chamada.
  const { data: arenas } = useQuery({
    queryKey: ['arenas'],
    queryFn: () => authApi.arenas(),
    enabled: Boolean(eu),
  });

  useEffect(() => {
    document.body.classList.toggle('no-notes', !mostrarNotas);
  }, [mostrarNotas]);

  if (!eu) return null;

  const grupos = menuVisivel(pode);
  const arenaAtual = arenas?.find((a) => a.arenaId === eu.arenaId);
  const nomeDaArena = arenaAtual?.nome ?? 'Sua arena';
  const nomeDoPerfil = arenaAtual?.perfilNome ?? eu.perfil.chave;
  const itemAtual = MENU.flatMap((g) => g.itens).find(
    (i) => local.pathname === i.rota || local.pathname.startsWith(`${i.rota}/`),
  );

  return (
    <div className="app">
      <aside className="side">
        <div className="brand">
          <img className="mark" src="/brand/simbolo.webp" alt="" />
          <b>Sand Sports</b>
        </div>

        <button
          className="arena"
          onClick={() => setTrocandoArena(true)}
          aria-label={`Arena ${nomeDaArena}. Trocar de arena`}
        >
          <span className="sigla">{siglaDe(nomeDaArena)}</span>
          <span className="quem">
            <b>{nomeDaArena}</b>
            <small>{nomeDoPerfil}</small>
          </span>
          <Icone nome="swap" className="ic swap" />
        </button>

        <nav className="nav sb">
          {grupos.map((grupo) => (
            <div key={grupo.titulo}>
              <h4>{grupo.titulo}</h4>
              {grupo.itens.map((item) => (
                // O NavLink já marca o ativo com `aria-current="page"`,
                // que é ao mesmo tempo o seletor do CSS e o que o leitor
                // de tela anuncia. Uma classe `.ativo` daria só o primeiro.
                <NavLink key={item.rota} to={item.rota}>
                  <span className="chip">
                    <Icone nome={item.icone} />
                  </span>
                  {item.rotulo}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="side-foot">
          <div className="usuario">
            <Avatar nome={eu.usuario.nome} />
            <span className="quem">
              <b>{eu.usuario.nome}</b>
              <small>{nomeDoPerfil}</small>
            </span>
            <button
              className="sair"
              onClick={() => void sair().then(() => navegar('/login'))}
              title="Sair"
              aria-label="Sair"
            >
              <Icone nome="sair" />
            </button>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              className="ck"
              checked={mostrarNotas}
              onChange={(e) => setMostrarNotas(e.target.checked)}
            />
            Mostrar notas do protótipo
          </label>
        </div>
      </aside>

      <div>
        <header className="top">
          <nav className="crumbs" aria-label="Você está em">
            <span>{nomeDaArena}</span>
            {itemAtual && (
              <>
                <i aria-hidden="true">/</i>
                <b>{itemAtual.rotulo}</b>
              </>
            )}
          </nav>

          <span className="spacer" />

          <BuscaGlobal />

          <TemaToggle />

          <BotaoDeIcone icone="bell" aria-label="Alertas" comAviso />

          {/* O rodapé do menu, onde fica o "Sair", não existe no celular. */}
          <button
            type="button"
            className="icon-btn so-mobile"
            aria-label="Sair"
            onClick={() => void sair().then(() => navegar('/login'))}
          >
            <Icone nome="sair" />
          </button>

          {/*
            "Nova reserva" exige a AÇÃO de criar na agenda. O Professor
            tem `visualizar` — ações `['ler']` —, então o botão some
            para ele.
          */}
          {pode('agenda', 'criar') && (
            <Botao icone="plus" onClick={() => navegar('/agenda?nova=1')}>
              Nova reserva
            </Botao>
          )}
        </header>

        <main className="main">
          <Outlet />
        </main>
      </div>

      {trocandoArena && (
        <TrocaDeArena
          onFechar={() => setTrocandoArena(false)}
          onTrocou={(nome) => {
            setTrocandoArena(false);
            avisar(`Você está em ${nome}.`);
          }}
        />
      )}
    </div>
  );
}

/**
 * O campo único da topbar — "Buscar aluno, comanda ou reserva" — sobre o
 * registry de `shell/buscaGlobal.ts`. `Shell` não conhece `alunos`: cada
 * fonte se registra no módulo dela (`alunos/buscaGlobal.ts` é a
 * referência) e aparece aqui como mais um grupo de resultados.
 *
 * O atraso e o mínimo de caracteres já estão em `useBuscaGlobal` — nada
 * dispara com menos de duas letras, "a" varreria a base inteira à toa.
 *
 * Ctrl+K (ou ⌘K) leva o foco para o campo, de qualquer tela.
 */
function BuscaGlobal() {
  const { pode } = useSessao();
  const navegar = useNavigate();
  const [termo, setTermo] = useState('');
  const [aberta, setAberta] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLInputElement>(null);
  const { carregando, grupos } = useBuscaGlobal(termo, pode);

  useEffect(() => {
    function aoClicarFora(evento: MouseEvent) {
      if (raiz.current && !raiz.current.contains(evento.target as Node)) setAberta(false);
    }
    function aoTeclar(evento: KeyboardEvent) {
      if ((evento.ctrlKey || evento.metaKey) && evento.key.toLowerCase() === 'k') {
        evento.preventDefault();
        campo.current?.focus();
      }
    }
    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, []);

  function ir(resultado: ResultadoDeBusca) {
    navegar(resultado.rota);
    setTermo('');
    setAberta(false);
  }

  const mostrarPainel = aberta && termo.trim().length >= 2;

  return (
    <div ref={raiz} style={{ position: 'relative', flex: '0 1 320px', minWidth: 200 }}>
      <label className="search" style={{ flex: 'none', width: '100%' }}>
        <Icone nome="search" />
        <input
          ref={campo}
          placeholder="Buscar aluno, comanda ou reserva"
          aria-label="Buscar"
          value={termo}
          onChange={(e) => {
            setTermo(e.target.value);
            setAberta(true);
          }}
          onFocus={() => setAberta(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setAberta(false);
          }}
        />
        <kbd aria-hidden="true">Ctrl K</kbd>
      </label>

      {mostrarPainel && (
        <div
          role="listbox"
          aria-label="Resultados da busca"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 38,
            zIndex: 5,
            maxHeight: 360,
            overflowY: 'auto',
            background: 'var(--panel)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r)',
            boxShadow: 'var(--pop)',
          }}
        >
          {carregando && grupos.length === 0 && (
            <div style={{ padding: '12px 14px', color: 'var(--dim)', fontSize: 13 }}>Buscando…</div>
          )}
          {!carregando && grupos.length === 0 && (
            <div style={{ padding: '12px 14px', color: 'var(--dim)', fontSize: 13 }}>
              Nada encontrado.
            </div>
          )}
          {grupos.map(({ fonte, itens }) => (
            <Fragment key={fonte.chave}>
              <div
                className="mono"
                style={{
                  padding: '8px 14px 4px',
                  fontSize: 11,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                  color: 'var(--dim)',
                }}
              >
                {fonte.rotulo}
              </div>
              {itens.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => ir(r)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    padding: '9px 14px',
                    background: 'transparent',
                    border: 'none',
                    borderTop: '1px solid var(--line)',
                    color: 'var(--txt)',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <Icone nome={r.icone} />
                  <span style={{ display: 'flex', flexDirection: 'column' }}>
                    <b style={{ fontSize: 14, fontWeight: 500 }}>{r.titulo}</b>
                    {r.subtitulo && (
                      <small style={{ color: 'var(--dim)', fontSize: 12 }}>{r.subtitulo}</small>
                    )}
                  </span>
                </button>
              ))}
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * O menu de troca de arena, aberto pelo seletor da sidebar.
 *
 * Trocar de arena emite um par de tokens NOVO e encerra a família
 * anterior — por isso a página recarrega o `eu` em vez de tentar
 * remendar o estado local.
 */
function TrocaDeArena({
  onFechar,
  onTrocou,
}: {
  onFechar: () => void;
  onTrocou: (nome: string) => void;
}) {
  const { eu, recarregar } = useSessao();
  const { data, isPending, isError } = useQuery({
    queryKey: ['arenas'],
    queryFn: () => authApi.arenas(),
  });

  return (
    <Modal aberto onFechar={onFechar} titulo="Trocar de arena" largura="estreita">
      <CorpoDeModal>
        {isPending && <p className="sub">Carregando…</p>}
        {isError && <p className="sub">Não consegui listar suas arenas.</p>}
        <div className="roles" style={{ marginTop: 0 }}>
          {data?.map((arena) => (
            <button
              key={arena.arenaId}
              className="role"
              disabled={arena.arenaId === eu?.arenaId}
              onClick={async () => {
                tokens.gravar(await authApi.trocarArena(arena.arenaId));
                await recarregar();
                onTrocou(arena.nome);
              }}
            >
              <span className="chip">{siglaDe(arena.nome)}</span>
              <span>
                <b>{arena.nome}</b>
                <small>{arena.perfilNome}</small>
              </span>
              {arena.arenaId === eu?.arenaId ? (
                <Icone nome="check" className="ic go" />
              ) : (
                <Icone nome="go" className="ic go" />
              )}
            </button>
          ))}
        </div>
      </CorpoDeModal>
    </Modal>
  );
}
